import type { ReactElement } from 'react';
import { useCallback } from 'react';

import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useAfterLoggedInTarget } from '#src/hooks/useAfterLoggedInTarget.ts';
import { client as httpClient, onError } from '#src/lib/http.ts';
import { paramsToSnakeCase } from '#src/lib/toSnakeCase.ts';

import type { AuthResponse, UpdateUserAttributes } from './index.ts';
import { AuthContext } from './index.ts';

type Props = {
  children: ReactElement;
};

export const AuthProvider = ({ children }: Props) => {
  const { refetch } = useCurrentUserContext();
  const fromPath = useAfterLoggedInTarget();

  const updateUser = useCallback(
    async (attributes: UpdateUserAttributes): Promise<AuthResponse> => {
      const { passwordHash, currentPasswordHash, ...rest } = attributes;
      const params: UpdateUserAttributes & {
        currentPassword?: string;
        password?: string;
      } = rest;
      if (passwordHash) {
        params.password = passwordHash;
        params.currentPassword = passwordHash;
      }
      if (currentPasswordHash) params.currentPassword = currentPasswordHash;

      try {
        await httpClient.put('/users.json', {
          user: paramsToSnakeCase(params),
        });
        await refetch();
        return {};
      } catch (error) {
        return onError(error);
      }
    },
    [refetch]
  );

  const createResetPasswordRequest = useCallback(
    async (email: string, recaptchaToken: string): Promise<AuthResponse> => {
      try {
        await httpClient.post('/users/password.json', {
          user: {
            email,
            from_path: fromPath,
            recaptcha_token_v2: recaptchaToken,
          },
        });
        return {};
      } catch (error) {
        return onError(error);
      }
    },
    [fromPath]
  );

  const resetPassword = useCallback(
    async (passwordHash: string, token: string): Promise<AuthResponse> => {
      try {
        await httpClient.put('/users/password.json', {
          user: {
            reset_password_token: token,
            password: passwordHash,
            password_confirmation: passwordHash,
          },
        });
        return {};
      } catch (error) {
        return onError(error);
      }
    },
    []
  );

  return (
    <AuthContext
      value={{
        updateUser,
        createResetPasswordRequest,
        resetPassword,
      }}
    >
      {children}
    </AuthContext>
  );
};
